import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Tag } from 'antd';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import { useTranslation } from 'react-i18next';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import { useOptionsDataGridFilter } from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { IconButton } from '../../../../common/controls/Controls';
import { resolvePhdThesesViewConfig } from './Configurations/phdThesesViewConfig.js';
import {
  LikeOutlined,
  DownloadOutlined,
  DislikeOutlined,
  MessageOutlined,
  EditOutlined,
  FileSearchOutlined,
  CreditCardOutlined,
  RedoOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { renderPhdAbbreviatedValue } from '../utils/renderPhdAbbreviatedValue.jsx';

const PhdThesesTable = ({
  theses,
  status,
  showSupervisorColumn = true,
  showSpecialistColumn = true,
  canDenyRow,
  onDetailClick,
  onDelete,
  onApprove,
  onDeny,
  onArchive,
  onDefend,
  onRestore,
  onCreateComment,
  canCreateCommentRow,
  onDownload,
  onView,
  onPreview,
  onCopy,
  canEditRow,
  canApproveRow,
  approveDisabledTooltip,
  rowSelection,
  canArchiveRow,
  canDefendRow,
  canRestoreRow,
  canDeleteRow,
  canCopyRow,
  onReplace,
  canReplaceRow,
  editDisabledTooltip,
  canDownloadRow,
  canViewRow,
  expandedRowKeys,
  onExpandedRowsChange,
  renderExpandedRow,
  pagination,
  onPaginationChange,
}) => {
  const { t } = useTranslation();
  const viewConfig = resolvePhdThesesViewConfig(status);

  const supervisorFilter = useDataGridTextFilter('supervisor_label');
  const specialistFilter = useDataGridTextFilter('specialist_label');
  const buildOptionsFromRows = useMemo(
    () => (field) => {
      const labels = Array.from(
        new Set(
          (theses || [])
            .map((row) => row?.[field])
            .filter(
              (value) => value !== null && value !== undefined && value !== '',
            ),
        ),
      ).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }),
      );

      return labels.reduce((acc, label) => {
        acc[label] = label;
        return acc;
      }, {});
    },
    [theses],
  );

  const schoolYearOptions = useMemo(
    () => buildOptionsFromRows('school_year_label'),
    [buildOptionsFromRows],
  );
  const studyFieldOptions = useMemo(
    () => buildOptionsFromRows('study_program_label'),
    [buildOptionsFromRows],
  );
  const statusOptions = useMemo(
    () => ({
      [t('Čaká na schválenie')]: t('Čaká na schválenie'),
      [t('Schválená')]: t('Schválená'),
      [t('Obhájená')]: t('Obhájená'),
      [t('Neschválená')]: t('Neschválená'),
      [t('Archivovaná')]: t('Archivovaná'),
    }),
    [t],
  );

  const schoolYearFilter = useOptionsDataGridFilter(
    'school_year_label',
    schoolYearOptions,
  );
  const programFilter = useOptionsDataGridFilter(
    'study_program_label',
    studyFieldOptions,
  );
  const statusFilter = useOptionsDataGridFilter('status_label', statusOptions);
  const resolveRowStatus = (row) => {
    if (row?.status_code) {
      return row.status_code;
    }
    if (row?.archived_at) {
      return row?.approved_at ? 'archived' : 'denied';
    }
    return row?.approved_at ? 'approved' : 'pending';
  };
  const getStatusTagColor = (row) => {
    switch (resolveRowStatus(row)) {
      case 'approved':
        return 'green';
      case 'defended':
        return 'blue';
      case 'denied':
        return 'red';
      case 'archived':
        return 'default';
      default:
        return 'orange';
    }
  };

  const columns = [
    {
      title: t('Školský rok'),
      dataIndex: 'school_year_label',
      key: 'school_year_label',
      ellipsis: true,
      ...schoolYearFilter,
    },
    {
      title: t('Názov'),
      dataIndex: 'title_sk',
      key: 'title_sk',
      width: status === 'pending' ? 320 : undefined,
      ellipsis: true,
    },
    ...(showSupervisorColumn
      ? [
          {
            title: t('Školiteľ'),
            dataIndex: 'supervisor_label',
            key: 'supervisor_label',
            ellipsis: true,
            ...supervisorFilter,
          },
        ]
      : []),
    ...(showSpecialistColumn
      ? [
          {
            title: t('Špecialista'),
            dataIndex: 'specialist_label',
            key: 'specialist_label',
            ellipsis: true,
            ...specialistFilter,
          },
        ]
      : []),
    {
      title: t('Študijný odbor'),
      dataIndex: 'study_program_label',
      key: 'study_program_label',
      ellipsis: true,
      render: (value, row) =>
        renderPhdAbbreviatedValue(value, row.study_program_full_label),
      ...programFilter,
    },
  ];

  if (status === 'archived') {
    columns.push({
      title: t('Archivované'),
      dataIndex: 'archived_at',
      key: 'archived_at',
      ellipsis: true,
      render: (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '—'),
    });
  }

  columns.push({
    title: t('Stav'),
    dataIndex: 'status_label',
    key: 'status_label',
    ellipsis: true,
    render: (value, row) => <Tag color={getStatusTagColor(row)}>{value}</Tag>,
    ...statusFilter,
  });

  const customSettings = (row) => (
    <div style={{ display: 'flex' }}>
      {onDetailClick && (
        <IconButton
          icon={<EditOutlined />}
          tooltip={t('Upraviť')}
          disabled={Boolean(canEditRow && !canEditRow(row))}
          disabledTooltip={
            canEditRow && !canEditRow(row)
              ? editDisabledTooltip?.(row) || t('Upraviť nie je dostupné')
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (canEditRow && !canEditRow(row)) {
              return;
            }
            onDetailClick(row);
          }}
        />
      )}
      {onView && (
        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Zobraziť detail')}
          disabled={Boolean(canViewRow && !canViewRow(row))}
          disabledTooltip={
            canViewRow && !canViewRow(row)
              ? t('Detail nie je dostupný')
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (canViewRow && !canViewRow(row)) {
              return;
            }
            onView(row);
          }}
        />
      )}
      {status === 'archived' && row?.defended_at && onPreview && (
        <IconButton
          icon={<EyeOutlined />}
          tooltip={t('Zobraziť verejný detail')}
          onClick={(e) => {
            e.stopPropagation();
            onPreview(row);
          }}
        />
      )}
      {viewConfig.showDownload &&
        ((status === 'active' || status === 'mine')
          ? resolveRowStatus(row) === 'approved'
          : status === 'archived' && row?.is_frozen) &&
        onDownload && (
          <IconButton
            icon={<DownloadOutlined />}
            tooltip={t('Stiahnuť DOCX')}
            disabled={Boolean(canDownloadRow && !canDownloadRow(row))}
            disabledTooltip={
              canDownloadRow && !canDownloadRow(row)
                ? t('Sťahovanie nie je dostupné')
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              if (canDownloadRow && !canDownloadRow(row)) {
                return;
              }
              onDownload(row);
            }}
          />
        )}
      {viewConfig.showApprove && status === 'pending' && onApprove && (
        <IconButton
          icon={<LikeOutlined />}
          tooltip={t('Schváliť')}
          disabled={Boolean(canApproveRow && !canApproveRow(row))}
          disabledTooltip={
            canApproveRow && !canApproveRow(row)
              ? approveDisabledTooltip?.(row) || t('Schválenie nie je povolené')
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (canApproveRow && !canApproveRow(row)) {
              return;
            }
            onApprove(row);
          }}
        />
      )}
      {viewConfig.showDeny && status === 'pending' && onDeny && (
        <IconButton
          icon={<DislikeOutlined />}
          tooltip={t('Zamietnuť')}
          disabled={Boolean(canDenyRow && !canDenyRow(row))}
          disabledTooltip={
            canDenyRow && !canDenyRow(row)
              ? t('Zamietnutie nie je povolené')
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (canDenyRow && !canDenyRow(row)) {
              return;
            }
            onDeny(row);
          }}
        />
      )}
      {status === 'pending' && onCreateComment && (
        <IconButton
          icon={<MessageOutlined />}
          tooltip={t('Pridať komentár')}
          disabled={Boolean(canCreateCommentRow && !canCreateCommentRow(row))}
          disabledTooltip={
            canCreateCommentRow && !canCreateCommentRow(row)
              ? t('Komentovanie nie je dostupné')
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (canCreateCommentRow && !canCreateCommentRow(row)) {
              return;
            }
            onCreateComment(row);
          }}
        />
      )}
      {viewConfig.showArchive &&
        (status === 'active' || status === 'mine') &&
        resolveRowStatus(row) === 'approved' &&
        onArchive && (
          <IconButton
            icon={<CreditCardOutlined />}
            tooltip={t('Archivovať')}
            disabled={Boolean(canArchiveRow && !canArchiveRow(row))}
            disabledTooltip={
              canArchiveRow && !canArchiveRow(row)
                ? t('Archivácia nie je povolená')
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              if (canArchiveRow && !canArchiveRow(row)) {
                return;
              }
              onArchive(row);
            }}
          />
        )}
      {(status === 'active' || status === 'mine') &&
        resolveRowStatus(row) === 'approved' &&
        !row?.defended_at &&
        onDefend && (
          <IconButton
            icon={<CheckCircleOutlined />}
            tooltip={t('Označiť ako obhájenú')}
            disabled={Boolean(canDefendRow && !canDefendRow(row))}
            disabledTooltip={
              canDefendRow && !canDefendRow(row)
                ? t(
                    'Obhajobu je možné označiť len pri priradenej téme študentovi.',
                  )
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              if (canDefendRow && !canDefendRow(row)) {
                return;
              }
              onDefend(row);
            }}
          />
        )}
      {(status === 'active' || status === 'mine') &&
        resolveRowStatus(row) === 'approved' &&
        onReplace && (
          <IconButton
            icon={<SwapOutlined />}
            tooltip={t('Nahradiť dizertačnú tému')}
            disabled={Boolean(canReplaceRow && !canReplaceRow(row))}
            disabledTooltip={
              canReplaceRow && !canReplaceRow(row)
                ? t('Nahradiť možno iba aktuálnu priradenú tému')
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              if (canReplaceRow && !canReplaceRow(row)) {
                return;
              }
              onReplace(row);
            }}
          />
        )}
      {viewConfig.showCopy &&
        status === 'archived' &&
        row?.defended_at &&
        onCopy && (
          <IconButton
            icon={<i className="fa fa-copy" />}
            tooltip={t('Duplikovať do aktuálneho školského roku')}
            disabled={Boolean(canCopyRow && !canCopyRow(row))}
            disabledTooltip={
              canCopyRow && !canCopyRow(row)
                ? t('Kopírovanie nie je povolené')
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              if (canCopyRow && !canCopyRow(row)) {
                return;
              }
              onCopy(row);
            }}
          />
        )}
      {viewConfig.showRestore &&
        (status === 'archived' || status === 'mine') &&
        resolveRowStatus(row) !== 'pending' &&
        resolveRowStatus(row) !== 'approved' &&
        row?.archived_at &&
        onRestore && (
          <IconButton
            icon={<RedoOutlined />}
            tooltip={t('Obnoviť')}
            disabled={Boolean(canRestoreRow && !canRestoreRow(row))}
            disabledTooltip={
              canRestoreRow && !canRestoreRow(row)
                ? row?.archive_reason_keys?.some((reason) =>
                    ['student_finished', 'student_expelled'].includes(reason),
                  )
                  ? t(
                      'Tému priradenú ukončenému alebo vylúčenému doktorandovi nemožno obnoviť.',
                    )
                  : t('Obnovenie nie je povolené')
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              if (canRestoreRow && !canRestoreRow(row)) {
                return;
              }
              onRestore(row);
            }}
          />
        )}
    </div>
  );

  const handleViewClick = (row) => {
    if (onView && (!canViewRow || canViewRow(row))) {
      onView(row);
    }
  };

  const handleDetailClick = (row) => {
    if (onDetailClick && (!canEditRow || canEditRow(row))) {
      onDetailClick(row);
    } else {
      handleViewClick(row);
    }
  };

  return (
    <DataGrid
      locale={{
        emptyText: t(
          'Pre zvolený pohľad a filtre nie sú dostupné žiadne záznamy.',
        ),
      }}
      rowKey={(thesis) => thesis.id}
      columns={columns}
      dataSource={theses || []}
      permissionsBasic={'phd-theses'}
      uniqueTableId={'phd-theses-table'}
      customSettings={customSettings}
      onEditClick={undefined}
      onRowClick={onDetailClick || onView ? handleDetailClick : undefined}
      onRowDetailClick={onView ? handleViewClick : undefined}
      onDelete={onDelete}
      deleteCondition={canDeleteRow}
      secondaryCondition={canEditRow}
      editDisabledTooltip={editDisabledTooltip}
      rowSelection={rowSelection}
      disableExpandRow={(row) =>
        !(renderExpandedRow && (row?.comments_count || 0) > 0)
      }
      customExpandedRowRender={
        renderExpandedRow ? (row) => renderExpandedRow(row) : undefined
      }
      expandedRowKeys={expandedRowKeys}
      onExpandedRowsChange={onExpandedRowsChange}
      pagination={pagination}
      onChange={onPaginationChange}
    />
  );
};

export default PhdThesesTable;
